/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import React, { useMemo, useState } from 'react';
import { Button, Offcanvas, OffcanvasBody, OffcanvasHeader } from 'reactstrap';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { CustomTag, UNWRAP_IXBRL_COMMAND, WRAP_IXBRL_COMMAND } from './command';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { VirtualMenuList } from '@g17eco/molecules/select/partials/VirtualMenuList';
import IconButton from '@g17eco/molecules/button/IconButton';
import { ReviewTagForm } from '@features/rich-text-editor/plugins/CustomTagPlugin/ReviewTagForm';
import { getMaxIdsFromEditor } from './contextUtils';
import { MetricSelector } from './MetricSelector';
import type { ReportDocumentUtrv } from '@api/initiative-report-documents';
import type { SelectedUtrv } from './types';
import { UtrValueType } from '@g17eco/types/universalTracker';
import { NumberScale } from '@g17eco/types/units';
import type { IXBRLNodeProps } from './IXBRLNode';

interface Props {
  tags: CustomTag[];
  isOpen: boolean;
  toggle: () => void;
  selectionTags?: string[];
  utrvs: ReportDocumentUtrv[];
}

export const numberScaleValueMap: Record<string, number> = {
  [NumberScale.Single]: 0,
  [NumberScale.Hundreds]: 2,
  [NumberScale.Thousands]: 3,
  [NumberScale.Millions]: 6,
  [NumberScale.Billions]: 9,
};

const getTagProps = ({
  tagName,
  maxFactId,
  maxUnitRef,
  maxContextRef,
  selectedUtrv,
}: {
  tagName: string;
  selectedUtrv: SelectedUtrv;
  maxFactId: number;
  maxUnitRef: number;
  maxContextRef: number;
}): IXBRLNodeProps => {
  const {
    universalTracker: { valueType, numberScale },
  } = selectedUtrv;
  const commonProps = {
    name: tagName,
    contextRef: `c-${maxContextRef + 1}`,
    factId: `fact-${maxFactId + 1}`,
    utrvId: selectedUtrv.utrvId,
    valueListCode: selectedUtrv.valueListCode,
  };
  if (
    valueType === UtrValueType.Number ||
    valueType === UtrValueType.Percentage ||
    valueType === UtrValueType.NumericValueList
  ) {
    return {
      ...commonProps,
      tag: 'ix:nonFraction',
      format: 'ixt4:num-dot-decimal',
      decimals: 2,
      scale: numberScaleValueMap[numberScale ?? NumberScale.Single],
      unitRef: `u-${maxUnitRef + 1}`,
    };
  } else {
    // Non-numeric values - use text format
    return {
      ...commonProps,
      tag: 'ix:nonNumeric',
    };
  }
};

export function TagSidebar({ isOpen, tags, toggle, selectionTags = [], utrvs }: Readonly<Props>) {
  const [tagName, setTagName] = useState('');
  const [selectedUtrv, setSelectedUtrv] = useState<SelectedUtrv>();
  const [editor] = useLexicalComposerContext();
  const options = useMemo(() => {
    return tags.map((tag) => ({ value: tag.tagName, label: tag.tagName }));
  }, [tags]);

  const selectedTags = tags.filter((tag) => selectionTags.includes(tag.tagName));

  const handleApplyTag = () => {
    if (!selectedUtrv || !tagName) {
      return;
    }
    const { maxFactId, maxUnitRef, maxContextRef } = getMaxIdsFromEditor(editor);

    // This should be decided based on the tag information
    const xbrlTag: 'ix:nonFraction' | 'ix:nonNumeric' = 'ix:nonFraction';

    editor.dispatchCommand(WRAP_IXBRL_COMMAND, {
      tagName: xbrlTag,
      properties: getTagProps({ tagName, selectedUtrv, maxFactId, maxUnitRef, maxContextRef }),
    });
    setTagName('');
    setSelectedUtrv(undefined);
    toggle();
  };

  const handleRemoveTag = (tagName: string | null) => {
    editor.dispatchCommand(UNWRAP_IXBRL_COMMAND, tagName ? [tagName] : null);
  };

  const value = options.find((option) => option.value === tagName) ?? null;
  const closeBtn = (
    <IconButton color={'secondary'} icon={'fa-light fa-sidebar-flip'} onClick={toggle} className='border-0' />
  );

  return (
    <Offcanvas direction={'end'} returnFocusAfterClose={false} isOpen={isOpen} toggle={toggle}>
      <OffcanvasHeader close={closeBtn} toggle={toggle}>
        Review Tags
      </OffcanvasHeader>
      <OffcanvasBody>
        {selectedTags.length > 0 ? (
          <div className='mb-3'>
            <h6>Selected Tags</h6>
            <ReviewTagForm
              tags={selectedTags}
              onAction={({ action, tag, shouldClose }) => {
                // Only remove action is available
                if (action === 'remove') {
                  handleRemoveTag(tag.tagName);
                }

                if (shouldClose) {
                  toggle();
                }
              }}
            />
            <hr />
          </div>
        ) : null}
        <div>
          <h6>Available Tags</h6>
          <div data-testid='tag-select'>
            <SelectFactory
              className={'mb-3'}
              selectType={SelectTypes.SingleSelect}
              options={options}
              onChange={(option) => {
                setTagName(option?.value ?? '');
              }}
              components={{ MenuList: VirtualMenuList }}
              value={value}
            />
          </div>
          <h6>Select Metric</h6>
          <MetricSelector utrvs={utrvs} selectedUtrv={selectedUtrv} onUtrvChange={setSelectedUtrv} />
          <Button color='primary' disabled={!tagName || !selectedUtrv} onClick={() => handleApplyTag()}>
            Apply Tag
          </Button>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
}
